import React, { useMemo, useState, useCallback } from "react";

import {
  isRequirementPolicy,
  RequirementList as RequirementListType,
} from "@design-controls/types";
import {
  createQColumnHelper,
  DataProvider,
  Filtering,
  QDataTable,
  TableMenuItem,
  QDeleteConfirmationModal,
  QText,
  Pagination,
} from "@qualio/ui-components";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { CategoryFilter } from "../../filter/CategoryFilter";
import { ShowOnlyOpen } from "../../filter/ShowOnlyOpen";
import { ComponentFilter, RequirementType } from "./filter";
import { usePolicyGroupResourceQuickSearch } from "../../hooks/search/usePolicyGroupResourceQuickSearch";
import { useResourceLibraryQuickSearch } from "../../hooks/search/useResourceLibraryQuickSearch";
import { useCurrentConfigs } from "../../hooks/useCurrentConfigs";
import { useDeleteRequirement } from "../../hooks/designElement/useDeleteRequirement";
import { useRequirementList } from "../../hooks/designElement/useRequirementsList";
import { GapTableCell } from "../../components/GapTableCell/GapTableCell";
import { designElementStatusColumn } from "../../components/DesignElementStatusColumn/DesignElementStatusColumn";
import { qriColumn } from "../../components/QRIColumn/QRIColumn";
import { filterByStatus, StatusFilter } from "../../filter/StatusFilter";
import useHasPermission from "../../hooks/useHasPermission";
import { UserPermissions } from "../../domain/permissions";
import { Link } from "react-router-dom";
import { useCurrentProduct } from "../../hooks/useCurrentProduct";

const columnHelper = createQColumnHelper<RequirementListType>();

export const RequirementList: React.FC = () => {
  const { t } = useTranslation();
  const { isLoading, data } = useRequirementList();
  const { id: productId } = useCurrentProduct();
  const { isLoading: isDeleting, mutate: deleteElement } =
    useDeleteRequirement();
  const [itemToDelete, setItemToDelete] = useState<RequirementListType | null>(
    null,
  );
  const navigate = useNavigate();
  const permissions = useHasPermission([
    UserPermissions.EDIT_DESIGN_ELEMENTS,
    UserPermissions.DELETE_DESIGN_ELEMENTS,
  ]);

  const { isLoading: categoryIsLoading, data: categories } =
    usePolicyGroupResourceQuickSearch(isRequirementPolicy);

  const policies = useCurrentConfigs();
  const requirementPolicy = policies.filter(isRequirementPolicy);
  const { isLoading: componentIsLoading, data: components } =
    useResourceLibraryQuickSearch(
      requirementPolicy?.[0].component?.[0].resourceIdentifier as string,
    );

  const columns = useMemo(() => {
    return [
      columnHelper.code("code", {
        header: t("requirements.list.table.header.id"),
        maxWidth: "100px",
      }),
      {
        header: " ",
        meta: {
          width: "100px",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: RequirementListType };
        }) => <GapTableCell openIssues={row.policyIssues} type={row.type} />,
      },
      columnHelper.custom("title", {
        header: t("requirements.list.table.header.requirement"),
        maxWidth: "400px",
        enableSorting: true,
        cell: (props) => {
          return <Link to={props.row.id}>{props.getValue()}</Link>;
        },
      }),
      qriColumn(
        "component",
        (record) => record.component?.label,
        t("requirements.list.table.header.component"),
      ),
      qriColumn(
        "category",
        (record) => record.category?.label,
        t("requirements.list.table.header.category"),
      ),
      columnHelper.date((record) => new Date(record.updated), {
        header: t("requirements.list.table.header.lastModified"),
        id: "updated",
        maxWidth: "150px",
      }),
      designElementStatusColumn(),
      columnHelper.menu({
        items: (
          <>
            <TableMenuItem
              onClick={(item: RequirementListType) =>
                navigate(`${item.id}/edit`)
              }
              isDisabled={(item: RequirementListType) =>
                !["product-development", "qualio"].includes(item.source)
              }
              data-cy="edit"
              isHidden={() =>
                !permissions[UserPermissions.EDIT_DESIGN_ELEMENTS]
              }
            >
              {t("requirements.list.actions.edit")}
            </TableMenuItem>
            <TableMenuItem
              onClick={(item: RequirementListType) => setItemToDelete(item)}
              color="red.500"
              data-cy="delete"
              isHidden={() =>
                !permissions[UserPermissions.DELETE_DESIGN_ELEMENTS]
              }
            >
              {t("requirements.list.actions.delete")}
            </TableMenuItem>
          </>
        ),
      }),
    ];
  }, [navigate, policies, permissions, t]);

  const filterDefinitions = useMemo(() => {
    return {
      type: {
        label: t("requirements.list.filters.requirementType"),
        schema: Filtering.schemas.StringSchema().array().nullish(),
        activeRender: (v: any) => {
          const item = requirementPolicy?.find((c) => c.type === v);
          if (!item) {
            return;
          }

          return {
            label: t("requirements.list.filterLabels.requirementType"),
            value: item.label,
          };
        },
      },
      component: {
        label: t("requirements.list.filters.component"),
        schema: Filtering.schemas.StringSchema().array().nullish(),
        activeRender: (v: any) => {
          const item = components?.results.find((c) => c.identifiers.qri === v);
          if (!item) {
            return;
          }

          return {
            label: t("requirements.list.filterLabels.component"),
            value: item.displayLabel,
          };
        },
      },
      category: {
        label: t("requirements.list.filters.category"),
        schema: Filtering.schemas.StringSchema().array().nullish(),
        activeRender: (v: any) => {
          const item = categories?.find((c) => c.identifiers.qri === v);
          if (!item) {
            return;
          }

          return {
            label: t("requirements.list.filterLabels.category"),
            value: item.displayLabel,
          };
        },
      },
      status: {
        label: t("requirements.list.filters.status"),
        schema: Filtering.schemas.StringSchema().array().nullish(),
      },
      show_required_child: {
        label: t("requirements.list.filters.showMissingChild"),
        schema: Filtering.schemas.BooleanSchema().nullish(),
        activeRender: (v: any) => ({
          label: null,
          value: v && t("requirements.list.filterLabels.missingChild"),
        }),
      },
      show_required_parent: {
        label: t("requirements.list.filters.showMissingParent"),
        schema: Filtering.schemas.BooleanSchema().nullish(),
        activeRender: (v: any) => ({
          label: null,
          value: v && t("requirements.list.filterLabels.missingParent"),
        }),
      },
      show_untested_requirement: {
        label: t("requirements.list.filters.showUntestedRequirement"),
        schema: Filtering.schemas.BooleanSchema().nullish(),
        activeRender: (v: any) => ({
          label: null,
          value: v && t("requirements.list.filterLabels.untestedRequirement"),
        }),
      },
    };
  }, [categories, components, requirementPolicy, t]);

  const filterOption = useCallback(
    (
      item: Readonly<RequirementListType>,
      searchTerm: string | undefined,
      filters: Filtering.ResolvedFilters<typeof filterDefinitions> | undefined,
    ) => {
      if (
        searchTerm &&
        !item.title
          .toLocaleLowerCase()
          .includes(searchTerm.toLocaleLowerCase()) &&
        !item.category?.label
          .toLocaleLowerCase()
          .includes(searchTerm.toLocaleLowerCase()) &&
        !item.component?.label
          .toLocaleLowerCase()
          .includes(searchTerm.toLocaleLowerCase()) &&
        !item.code?.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
      ) {
        return false;
      }

      if (filters && Object.values(filters).some((field) => field.value)) {
        const {
          show_required_child,
          show_required_parent,
          show_untested_requirement,
          type,
          category,
          component,
          status,
        } = filters;

        return (
          (!show_required_child?.value || item.policyIssues.REQUIRES_CHILD) &&
          (!show_required_parent?.value || item.policyIssues.REQUIRES_PARENT) &&
          (!show_untested_requirement?.value ||
            item.policyIssues.UNTESTED_REQUIREMENT) &&
          (!type?.value?.length || type.value.includes(item.type)) &&
          (!category?.value?.length ||
            category.value.includes(item.category?.qri)) &&
          (!status?.value?.length ||
            filterByStatus(item as any, status.value)) &&
          (!component?.value?.length ||
            component.value.includes(item.component?.qri))
        );
      }

      return true;
    },
    [],
  ) as DataProvider.FilterOptionFn<RequirementListType>;

  return (
    <>
      <Pagination.Auto pageSizeKey="size" pageIndexKey="page" clientSide>
        <Filtering.FilterProvider
          definitions={filterDefinitions}
          searchTermKey="search"
          persistanceKey={`Requirement-list-filter-state-${productId}`}
        >
          <DataProvider.Fixed
            data={data ?? []}
            isLoading={isLoading}
            filterOption={filterOption}
          >
            <QDataTable
              columns={columns}
              data-cy="requirement-list"
              getRowId={(row: RequirementListType) => row.id}
            >
              <Filtering.FormContent>
                <RequirementType />
                <ComponentFilter
                  isLoading={componentIsLoading}
                  components={components?.results}
                />
                <CategoryFilter
                  categories={categories}
                  isLoading={categoryIsLoading}
                />
                <StatusFilter />
                <ShowOnlyOpen type="requirement" />
              </Filtering.FormContent>
            </QDataTable>
          </DataProvider.Fixed>
        </Filtering.FilterProvider>
      </Pagination.Auto>
      {itemToDelete && (
        <QDeleteConfirmationModal
          title={t("requirements.delete.modalTitle")}
          onConfirm={() => {
            itemToDelete &&
              deleteElement(itemToDelete as any, {
                onSettled: () => setItemToDelete(null),
              });
          }}
          inProgress={isDeleting}
          onClose={() => setItemToDelete(null)}
          message={
            <QText fontSize="sm">
              {t("requirements.delete.confirmMessage", {
                requirementCodeTitle: itemToDelete.codeTitle,
              })}
            </QText>
          }
        />
      )}
    </>
  );
};
